// How the admin index is grouped: by the rank a thing needs, least privileged first.
//
// The console used to be one flat grid in which a lead's screens and an admin's screens
// were interleaved in whatever order the registry happened to return them, each card
// carrying a small "lead" or "admin" chip that you had to read one card at a time. A lead
// looking for the two things they can actually use had to scan all of them.
//
// Grouping moves that distinction from a chip on every card to the shape of the page: the
// things you can use are at the top, and everything below the gap needs a rank you may not
// have. The chip then says nothing the heading has not already said, so it goes.
//
// THE GROUPS ARE DERIVED, NOT LISTED. `SECTION_ORDER` is the rank ladder, so a descriptor
// registered tomorrow at `agent` lands in its own group above `lead` with no change here.
// A rank nobody uses renders nothing — `group` returns only non-empty sections.
//
// Pure, and in a `.js` file rather than the `.jsx` component, because the node test runner
// cannot import JSX (the same split as web/src/components/create/custom-field-values.js).

import { supportPath } from '../../support-path.js';

/** Least privileged first — the order the sections appear down the page. */
export const SECTION_ORDER = ['viewer', 'collaborator', 'agent', 'lead', 'admin'];

/**
 * What each group is called. "and above" is not decoration: `requires: 'lead'` means a
 * lead OR an admin can use it, and a heading reading just "Leads" would tell an admin
 * their own tools were somewhere else.
 */
const SECTION_LABEL = {
  viewer: 'Everyone',
  collaborator: 'Collaborators and above',
  agent: 'Agents and above',
  lead: 'Leads and above',
  admin: 'Administrators only',
};

/**
 * One card. `requires` is the rank the API enforces; everything else is presentation.
 *
 * @typedef {{key: string, to: string, label: string, hint: string|null, requires: string}} Entry
 */

/**
 * Entries grouped into sections, least privileged first, empty sections dropped.
 *
 * An entry whose `requires` is not a known rank is put in the LAST section rather than
 * hidden. Hiding it would make a resource the server is perfectly willing to serve
 * invisible for the sake of a spelling, and the admin group is the safe place to put
 * something whose requirement we do not recognise.
 *
 * @param {Entry[]} entries
 * @returns {{requires: string, label: string, entries: Entry[]}[]}
 */
export function groupByRequirement(entries) {
  const buckets = new Map(SECTION_ORDER.map((rank) => [rank, []]));
  const fallback = SECTION_ORDER[SECTION_ORDER.length - 1];

  for (const entry of entries ?? []) {
    const rank = buckets.has(entry?.requires) ? entry.requires : fallback;
    buckets.get(rank).push(entry);
  }

  return SECTION_ORDER
    .map((requires) => ({ requires, label: SECTION_LABEL[requires], entries: buckets.get(requires) }))
    .filter((section) => section.entries.length > 0);
}

/**
 * The screens that are not descriptors, as entries.
 *
 * Each is here for its own reason and each reason is worth keeping, because "why is this
 * not just a descriptor?" is the first question a reader of this file asks:
 *
 *   * `schedules` — a whole week per agent, one of design §7's three screens that cannot
 *     be generic.
 *   * `settings` — the generic routes are keyed on a row id and would let a caller INSERT
 *     any key it liked, which is the one property the settings registry exists to deny.
 *   * `integrations` — writes a SEALED CREDENTIAL BLOB, draws its form from a registry the
 *     server holds rather than from a column list, and calls two endpoints the generic
 *     framework has no vocabulary for (`/test`, `/api-clients`).
 *
 * They are returned whatever `GET /api/admin` did, because a lead whose console index
 * failed to load still has a working schedule editor. PRESENTATION, NEVER AUTHORIZATION —
 * the API refuses; this list only decides what is drawn.
 */
export const STATIC_ENTRIES = [
  {
    key: 'schedules',
    to: supportPath('/admin/schedules'),
    label: 'Agent schedules',
    hint: 'Who is on shift, and when',
    requires: 'lead',
  },
  {
    key: 'settings',
    to: supportPath('/admin/settings'),
    label: 'Settings',
    hint: 'How the ticket and customer lists look',
    requires: 'admin',
  },
  {
    key: 'integrations',
    to: supportPath('/admin/integrations'),
    label: 'Integrations & API',
    hint: 'Zendesk bridge, partner API keys',
    requires: 'admin',
  },
];

/**
 * A descriptor from `GET /api/admin` as an entry.
 *
 * `columns` is `[{ key, label }]`, not strings — joining it directly rendered
 * "[object Object] · [object Object]" on screen once, the default stringification, which
 * never throws and so was only found by looking at the page.
 */
export function descriptorEntry(resource) {
  const columns = Array.isArray(resource?.columns) ? resource.columns : [];
  return {
    key: resource.name,
    to: supportPath(`/admin/${resource.name}`),
    label: resource.label ?? resource.name,
    hint: columns.length ? columns.map((c) => c.label ?? c.key).join(' · ') : null,
    requires: resource.requires,
  };
}

/** Every card on the index, static screens first within their own group. */
export function consoleSections(resources) {
  const entries = [...STATIC_ENTRIES, ...(resources ?? []).map(descriptorEntry)];
  return groupByRequirement(entries);
}
